// components/kursi-play-v2/KpLiveLessonSummaryView.tsx
// =============================================================================
// Kursi Play V2 — Lesson Summary Screen
// =============================================================================

import { useEffect, useRef } from 'react';
import {
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { router, usePathname } from 'expo-router';
import { theme } from '@/constants/theme';
import { buildRuntimeHomePath, buildRuntimeLessonPath, buildRuntimeProfilePath, buildRuntimeReviewPath, resolveRuntimeBase } from '@/features/unified-learning/runtime/runtimeRoutes';

const PRIMARY  = theme.colors.primary;
const BG       = '#0B0B14';
const SURFACE  = '#16142A';
const SURFACE2 = '#221F3D';
const SURFACE3 = '#2D2B50';
const TEXT     = '#F0EFF8';
const TEXT_MED = '#9896B4';
const GREEN    = '#10B981';
const AMBER    = '#F59E0B';

type Props = {
  lessonId:        string;
  slug:            string;
  userId:          string;
  guestSessionToken?: string;
  xpEarned:        number;
  totalWorldXp:    number;
  streakDays:      number;
  nextLessonId:    string | null;
  isWorldComplete: boolean;
  isGuest?:        boolean;
  // TASK 12: streak freeze event flags
  freezeAwarded?:   boolean;
  streakPreserved?: boolean;
  freezeUsed?:      boolean;
};

export function KpLiveLessonSummaryView({
  slug, userId, xpEarned, totalWorldXp,
  streakDays, nextLessonId, isWorldComplete, isGuest = false,
  freezeAwarded = false, streakPreserved = false, freezeUsed = false,
  guestSessionToken,
}: Props) {
  const scaleAnim = useRef(new Animated.Value(0.88)).current;
  const runtimeBase = resolveRuntimeBase(usePathname());
  const fadeAnim  = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(scaleAnim, { toValue: 1, useNativeDriver: true, speed: 6, bounciness: 8 }),
      Animated.timing(fadeAnim,  { toValue: 1, duration: 350, useNativeDriver: true }),
    ]).start();
  }, []);

  const runtimeSession = { uid: userId, token: guestSessionToken };

  const handleNext = () => {
    if (nextLessonId && !isWorldComplete) {
      router.replace(buildRuntimeLessonPath(runtimeBase, slug, nextLessonId, runtimeSession) as any);
    } else {
      router.replace(buildRuntimeHomePath(runtimeBase, slug, runtimeSession) as any);
    }
  };

  const handleHome = () => {
    router.replace(buildRuntimeHomePath(runtimeBase, slug, runtimeSession) as any);
  };

  const isComplete = isWorldComplete || (!nextLessonId);

  return (
    <View style={s.root}>
      <Animated.View style={{ flex: 1, opacity: fadeAnim }}>
        <ScrollView contentContainerStyle={s.content} showsVerticalScrollIndicator={false}>

          <Animated.View style={[s.card, { transform: [{ scale: scaleAnim }] }]}>

            {/* Top accent line */}
            <View style={[s.topLine, { backgroundColor: isWorldComplete ? GREEN : PRIMARY }]} />

            <View style={s.cardBody}>
              {/* Trophy / XP icon */}
              <View style={[s.iconWrap, { backgroundColor: (isWorldComplete ? GREEN : PRIMARY) + '18' }]}>
                <Text style={s.iconEmoji}>{isWorldComplete ? '🏆' : '⚡'}</Text>
              </View>

              <Text style={s.title}>
                {isWorldComplete ? 'העולם הושלם!' : 'שיעור הושלם!'}
              </Text>
              {isWorldComplete && (
                <Text style={s.subtitle}>השלמת את כל שיעורי העולם. עבודה מצוינת!</Text>
              )}

              {/* XP highlight */}
              <View style={[s.xpHighlight, { borderColor: AMBER + '44', backgroundColor: AMBER + '0C' }]}>
                <Text style={s.xpAmount}>+{xpEarned}</Text>
                <Text style={s.xpLabel}>XP הרווחת</Text>
              </View>

              {/* Stats */}
              <View style={s.statsSection}>
                <StatRow icon="🌟" label="סה״כ XP בעולם" value={`${totalWorldXp}`} valueColor={TEXT} />
                {streakDays > 1 && (
                  <StatRow icon="🔥" label="ימי סטריק" value={`${streakDays} ימים`} valueColor={GREEN} />
                )}
              </View>

              {/* TASK 12: Freeze event toasts */}
              {freezeAwarded && (
                <View style={s.freezeBanner}>
                  <Text style={s.freezeBannerIcon}>❄️</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.freezeBannerTitle}>הרווחת Streak Freeze!</Text>
                    <Text style={s.freezeBannerBody}>7 ימי סטריק ברצף — קיבלת קפאון סטריק. הוא ישמר אותך אם תפספס יום.</Text>
                  </View>
                </View>
              )}
              {freezeUsed && streakPreserved && (
                <View style={[s.freezeBanner, s.freezeBannerUsed]}>
                  <Text style={s.freezeBannerIcon}>🛡️</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.freezeBannerTitle}>הסטריק שלך הוצל!</Text>
                    <Text style={s.freezeBannerBody}>קפאון סטריק הופעל ושמר על הרצף שלך.</Text>
                  </View>
                </View>
              )}

              {/* Guest banner */}
              {isGuest && (
                <View style={s.guestBanner}>
                  <View style={s.guestBannerTop}>
                    <Text style={s.guestBannerIcon}>⚠</Text>
                    <Text style={s.guestBannerTitle}>ה-XP שלך לא נשמר!</Text>
                  </View>
                  <Text style={s.guestBannerBody}>
                    למדת +{xpEarned} XP — בלי חשבון זה ייעלם ברגע שתסגור את הדף.
                  </Text>
                  <Pressable
                    style={s.guestBtn}
                    onPress={() => router.push('/lead-gate' as any)}
                    accessibilityRole="button"
                  >
                    <Text style={s.guestBtnText}>שמור את ה-XP — חינם →</Text>
                  </Pressable>
                </View>
              )}

              {/* Actions */}
              <View style={s.actions}>
                {nextLessonId && !isWorldComplete && (
                  <Pressable style={s.primaryBtn} onPress={handleNext} accessibilityRole="button">
                    <Text style={s.primaryBtnText}>שיעור הבא ⟶</Text>
                  </Pressable>
                )}
                <Pressable
                  style={[s.secondaryBtn, !isComplete && s.secondaryBtnSmall]}
                  onPress={handleHome}
                  accessibilityRole="button"
                >
                  <Text style={s.secondaryBtnText}>
                    {isWorldComplete ? 'חזרה לדף הבית' : 'חזרה למסלול'}
                  </Text>
                </Pressable>
                <Pressable
                  style={[s.secondaryBtn, s.secondaryBtnSmall]}
                  onPress={() => router.push(buildRuntimeReviewPath(runtimeBase, slug, runtimeSession) as any)}
                  accessibilityRole="button"
                >
                  <Text style={s.secondaryBtnText}>סקירת טעויות</Text>
                </Pressable>
                <Pressable
                  style={s.profileLink}
                  onPress={() => router.push(buildRuntimeProfilePath(runtimeBase, runtimeSession) as any)}
                  accessibilityRole="button"
                >
                  <Text style={s.profileLinkText}>הפרופיל שלי →</Text>
                </Pressable>
              </View>
            </View>
          </Animated.View>

          <View style={{ height: 48 }} />
        </ScrollView>
      </Animated.View>
    </View>
  );
}

function StatRow({ icon, label, value, valueColor }: {
  icon: string; label: string; value: string; valueColor: string;
}) {
  return (
    <View style={s.statRow}>
      <Text style={[s.statValue, { color: valueColor }]}>{value}</Text>
      <Text style={s.statLabel}>{label}</Text>
      <Text style={s.statIcon}>{icon}</Text>
    </View>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  content: {
    padding: 20,
    paddingTop: Platform.OS === 'web' ? 56 : 48,
    maxWidth: 480,
    alignSelf: 'center' as any,
    width: '100%',
    alignItems: 'stretch',
  },

  card: {
    backgroundColor: SURFACE,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: SURFACE3,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 24px 60px rgba(0,0,0,0.6)' } as any) : {}),
  },
  topLine: { height: 3, width: '100%' },
  cardBody: { padding: 28, alignItems: 'center', gap: 18 },

  iconWrap: {
    width: 72, height: 72, borderRadius: 20,
    alignItems: 'center', justifyContent: 'center',
  },
  iconEmoji: { fontSize: 36 },

  title: {
    fontFamily: theme.fonts.bold, fontSize: 26, color: TEXT, textAlign: 'center', lineHeight: 34,
  },
  subtitle: {
    fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT_MED,
    textAlign: 'center', lineHeight: 22, maxWidth: 300,
    marginTop: -8,
  },

  xpHighlight: {
    borderRadius: 16, borderWidth: 1,
    paddingHorizontal: 28, paddingVertical: 14,
    alignItems: 'center', gap: 2, width: '100%',
  },
  xpAmount: {
    fontFamily: theme.fonts.bold, fontSize: 36, color: AMBER, lineHeight: 44,
  },
  xpLabel: {
    fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MED,
  },

  statsSection: {
    width: '100%', gap: 12,
    borderTopWidth: 1, borderTopColor: SURFACE3,
    paddingTop: 16,
  },
  statRow: { flexDirection: 'row-reverse', alignItems: 'center', gap: 10 },
  statIcon: { fontSize: 18, width: 26, textAlign: 'center' },
  statLabel: {
    flex: 1, fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT_MED, textAlign: 'right',
  },
  statValue: { fontFamily: theme.fonts.bold, fontSize: 18 },

  // Guest banner
  guestBanner: {
    width: '100%',
    backgroundColor: '#201505',
    borderWidth: 1,
    borderColor: '#D9770640',
    borderRadius: 14,
    padding: 14,
    gap: 10,
    alignItems: 'flex-end',
  },
  guestBannerTop: { flexDirection: 'row-reverse', alignItems: 'center', gap: 8, width: '100%' },
  guestBannerIcon: { fontSize: 16 },
  guestBannerTitle: {
    fontFamily: theme.fonts.bold, fontSize: 14, color: AMBER, textAlign: 'right',
  },
  guestBannerBody: {
    fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MED,
    textAlign: 'right', lineHeight: 19, width: '100%',
  },
  guestBtn: {
    backgroundColor: AMBER,
    borderRadius: 10,
    paddingVertical: 11,
    paddingHorizontal: 20,
    alignSelf: 'stretch',
    alignItems: 'center',
  },
  guestBtnText: { fontFamily: theme.fonts.bold, fontSize: 13, color: '#fff' },

  // Actions
  actions: {
    width: '100%', gap: 10,
    borderTopWidth: 1, borderTopColor: SURFACE3, paddingTop: 16,
  },
  primaryBtn: {
    backgroundColor: PRIMARY, borderRadius: 14, paddingVertical: 16,
    alignItems: 'center', justifyContent: 'center',
    ...(Platform.OS === 'web' ? ({ boxShadow: `0 6px 20px ${PRIMARY}55` } as any) : {}),
  },
  primaryBtnText: { fontFamily: theme.fonts.bold, fontSize: 15, color: '#fff' },
  secondaryBtn: {
    backgroundColor: SURFACE2, borderRadius: 14, paddingVertical: 15,
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 1, borderColor: SURFACE3,
  },
  secondaryBtnSmall: { paddingVertical: 12 },
  secondaryBtnText: { fontFamily: theme.fonts.medium, fontSize: 15, color: TEXT_MED },
  profileLink: { alignSelf: 'center', paddingVertical: 6 },
  profileLinkText: {
    fontFamily: theme.fonts.medium, fontSize: 13, color: TEXT_MED,
    textDecorationLine: 'underline',
  },

  // TASK 12: Freeze toasts
  freezeBanner: {
    flexDirection: 'row-reverse',
    gap: 12,
    alignItems: 'flex-start',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#60A5FA44',
    backgroundColor: '#0C1A3A',
    padding: 14,
    width: '100%',
  },
  freezeBannerUsed: {
    borderColor: `${GREEN}44`,
    backgroundColor: '#0D2A1A',
  },
  freezeBannerIcon: { fontSize: 24 },
  freezeBannerTitle: {
    fontFamily: theme.fonts.bold, fontSize: 14, color: '#93C5FD', textAlign: 'right',
  },
  freezeBannerBody: {
    fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MED, textAlign: 'right', marginTop: 3, lineHeight: 18,
  },
});
